// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTimeZone } from "@sequentech/ui-core";
import { useEventZonedFormat } from "@/hooks/useZonedFormat";
/** The Intl locale of an i18n language: Catalan is "cat" in the portals. */
export var intlLanguage = function (lang) { return (lang.split("-")[0] === "cat" ? "ca" : lang); };
var format = function (instant, zone, lang, options) {
    try {
        return new Intl.DateTimeFormat(lang, __assign(__assign({}, options), { timeZone: zone })).format(instant);
    }
    catch (_a) {
        return new Intl.DateTimeFormat(lang, __assign(__assign({}, options), { timeZone: "UTC" })).format(instant);
    }
};
/** The calendar day of `instant` in `zone`, to compare two instants. */
var dayIn = function (instant, zone) {
    return format(instant, zone, "en-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
};
/**
 * "6:15 PM CET": a time in the event's timezone (the election's on its
 * screens), labelled through the `timezones.*` texts. A time on another day
 * than `now` in that zone also shows its date: "Mar 13, 2028, 6:15 PM CET".
 */
export var useZonedTime = function (now) {
    var _a;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var zone = useEventZonedFormat().zone;
    var language = (_a = i18n === null || i18n === void 0 ? void 0 : i18n.language) !== null && _a !== void 0 ? _a : "en";
    var nowTime = now === null || now === void 0 ? void 0 : now.getTime();
    return useCallback(function (value) {
        if (!value)
            return "";
        var today = nowTime === undefined ? new Date() : new Date(nowTime);
        return formatDateTimeZone(value, zone, {
            t: t,
            lang: language,
            formatDateTime: function (instant, inZone) {
                return format(instant, inZone, intlLanguage(language), dayIn(instant, inZone) === dayIn(today, inZone)
                    ? { timeStyle: "short" }
                    : { dateStyle: "medium", timeStyle: "short" });
            },
        });
    }, [t, language, zone, nowTime]);
};
